import type { Metadata } from "next";

/**
 * L'origine del sito e la confezione delle card di condivisione.
 *
 * Fino a qui l'indirizzo assoluto del sito non era scritto da nessuna
 * parte: il layout dichiarava solo `title` e `description`, quindi ogni
 * campo di metadata che vuole un URL intero (il canonical degli
 * editoriali, l'`og:image` di un dispaccio) usciva relativo, e un
 * relativo in una card di condivisione non è risolvibile da chi la
 * legge — Facebook, LinkedIn, Telegram e X vogliono un URL assoluto.
 * `metadataBase` in app/layout.tsx risolve i relativi di tutte le
 * rotte, e sta qui perché l'origine la si cambia in un posto solo.
 *
 * In produzione si passa da variabile d'ambiente: il dominio vero non
 * è deducibile dal repo (a monte si chiama `nd.cesno.org`, l'anteprima
 * `oi.cesno.org`, il CDN delle foto `static-files.osservatorioitaliano.org`
 * e le caselle del footer `osservatorioitaliano.it`). Il ripiego sotto
 * è quest'ultimo, cioè l'unico dominio che il sito dichiara come
 * proprio; se quello di produzione è un altro, si scrive
 * `NEXT_PUBLIC_SITE_URL` in `.env.local` e non si tocca altro.
 */
export const SITE_URL = new URL(
  process.env.NEXT_PUBLIC_SITE_URL?.trim() || "https://www.osservatorioitaliano.it"
);

export const SITE_NAME = "Osservatorio Italiano";

/**
 * Le origini da cui il sito risponde davvero al pubblico, verificate a
 * mano. Le legge «Copia link» dell'editoriale
 * (components/editoriali/ComandiLettura.tsx), e ci sono perché
 * `SITE_URL` non basta: il suo ripiego è un dominio ipotizzato, e anche
 * una `NEXT_PUBLIC_SITE_URL` scritta male finirebbe negli appunti di chi
 * condivide come se fosse un indirizzo vero. La sola presenza della
 * variabile non dice che l'indirizzo risponde.
 *
 * Un'origine entra qui solo dopo averla provata su un articolo vero
 * (`curl -s -o /dev/null -w "%{http_code}"` sull'indirizzo intero: 200,
 * e la pagina è la nostra). Verificata il 02/10/2026:
 * `https://oi.cesno.org` (Caddy, poi il sito). Quando il dominio di
 * produzione sarà acceso e provato va aggiunto qui, e da quel momento
 * `NEXT_PUBLIC_SITE_URL` puntata su di lui diventa l'indirizzo copiato.
 */
export const ORIGINI_PUBBLICHE_VERIFICATE: readonly string[] = ["https://oi.cesno.org"];

/** L'origine canonica, ma solo se è fra quelle verificate: `null`
 *  altrimenti, e allora chi copia un link ripiega sull'origine corrente
 *  (o, da un'anteprima, su un'origine verificata). */
export const ORIGINE_CANONICA_VERIFICATA: string | null =
  process.env.NEXT_PUBLIC_SITE_URL?.trim() &&
  ORIGINI_PUBBLICHE_VERIFICATE.includes(SITE_URL.origin)
    ? SITE_URL.origin
    : null;

/** L'host delle foto dei dispacci (`image_url` a monte). Serve solo a
 *  sapere se possiamo dichiarare la misura: vedi `ogImmagini`. */
const CDN_FOTO = "static-files.osservatorioitaliano.org";

/**
 * L'og:image di riparo: la lastra col marchio, per le pagine che una
 * foto non ce l'hanno.
 *
 * Sono la maggioranza. La foto ce l'ha il dispaccio recente (e ce
 * l'ha sempre: `image_url` è valorizzato su tutto ciò che non è
 * archivio), mentre i dispacci d'archivio non ne hanno mai una, e non
 * ha niente da dare nessuna delle pagine scritte a mano: corridoi,
 * servizi, regioni, osint, cibernetica, gli indici, la ricerca.
 * Senza immagine la card di condivisione si rimpicciolisce a un
 * rettangolo di solo testo: su X e LinkedIn è la differenza fra mezzo
 * schermo e due righe grigie.
 *
 * Il disegno è a `app/og/riparo.png/route.tsx` e non è una foto
 * finta: è il marchio su fondo navy, cioè la stessa cosa che
 * `app/icon.svg` fa per la scheda del browser. Un'immagine uguale per
 * tutti ha senso finché dice il nome della testata e non finge di
 * illustrare l'articolo.
 *
 * Il percorso è relativo: lo risolve `metadataBase`, che è `SITE_URL`
 * qui sopra. Misure dichiarate perché sono fisse per costruzione, così
 * chi compone l'anteprima non deve scaricare il PNG per scoprirle.
 */
export const OG_RIPARO = "/og/riparo.png";
export const OG_MISURA = { width: 1200, height: 630 } as const;
const OG_RIPARO_ALT = `${SITE_NAME}: dispacci di geopolitica e OSINT`;

type OgBase = {
  title: string;
  description?: string;
  /** Percorso interno della pagina ("/paesi/albania"): `metadataBase`
   *  lo trasforma in URL assoluto. */
  url?: string;
  /** La foto, di solito l'`image_url` del dispaccio. È opzionale a
   *  monte e lo è anche qui: chi non ce l'ha non passa niente e si
   *  prende la lastra col marchio (`OG_RIPARO`). */
  image?: string | null;
  /** Testo alternativo della foto. Se non lo passi, il titolo. */
  imageAlt?: string;
};

/** La lastra col marchio, nella forma che vuole `openGraph.images`. */
function ogRiparo() {
  return [{ url: OG_RIPARO, alt: OG_RIPARO_ALT, ...OG_MISURA }];
}

/**
 * La lista `openGraph.images` a partire da un `image_url`.
 *
 * Le foto del CDN escono tutte da una pipeline sola e misurano
 * 1536×1024 (3:2): dichiarare `width`/`height` evita che chi compone
 * l'anteprima debba scaricare il file per scoprire il rapporto, ed è
 * il motivo per cui la misura la mettiamo solo per quell'host. Per una
 * foto di un altro host passiamo l'URL e basta.
 *
 * Senza foto, o con una foto che non è nemmeno un URL, si ripiega
 * sulla lastra: è l'unico posto in cui `OG_RIPARO` entra, così una
 * rotta non deve sapere che esiste, le basta passare da `ogSito` /
 * `ogArticolo` come già fa.
 */
function ogImmagini(image: string | null | undefined, alt: string) {
  const url = image?.trim();
  if (!url) return ogRiparo();

  let host: string | null = null;
  try {
    host = new URL(url, SITE_URL).hostname;
  } catch {
    // URL malformato a monte: la lastra, non un og:image rotto.
    return ogRiparo();
  }

  return host === CDN_FOTO
    ? [{ url, alt, width: 1536, height: 1024 }]
    : [{ url, alt }];
}

/**
 * Il blocco `openGraph` di una pagina del sito (indice, scheda paese,
 * regione, servizio…).
 *
 * Esiste perché i metadata si fondono in modo **piatto**: se una
 * pagina dichiara `openGraph`, quello del layout non viene integrato
 * ma sostituito. Quindi `siteName` e `locale` vanno ripetuti a ogni
 * rotta, e ripeterli a mano è il modo di farli divergere.
 */
export function ogSito(o: OgBase): Metadata["openGraph"] {
  return {
    type: "website",
    siteName: SITE_NAME,
    locale: "it_IT",
    title: o.title,
    description: o.description,
    url: o.url,
    images: ogImmagini(o.image, o.imageAlt ?? o.title),
  };
}

/** Come `ogSito`, ma per un pezzo datato: dispaccio, versione storica,
 *  editoriale. `publishedTime` vuole un ISO 8601. */
export function ogArticolo(
  o: OgBase & { publishedTime?: string; section?: string; authors?: string[] }
): Metadata["openGraph"] {
  return {
    type: "article",
    siteName: SITE_NAME,
    locale: "it_IT",
    title: o.title,
    description: o.description,
    url: o.url,
    publishedTime: o.publishedTime,
    section: o.section,
    authors: o.authors,
    images: ogImmagini(o.image, o.imageAlt ?? o.title),
  };
}
